import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceAreas = [
  { area: "Toronto", neighborhoods: "Downtown, Midtown, Uptown, East End, West End" },
  { area: "North York", neighborhoods: "Willowdale, Don Mills, York Mills, Bayview Village" },
  { area: "Scarborough", neighborhoods: "Agincourt, Malvern, Rouge, Birch Cliff" },
  { area: "Etobicoke", neighborhoods: "Islington, Mimico, Long Branch, Rexdale" },
  { area: "Mississauga", neighborhoods: "Port Credit, Streetsville, Erin Mills, Meadowvale" },
  { area: "Brampton", neighborhoods: "Downtown, Bramalea, Heart Lake, Mount Pleasant" },
  { area: "Richmond Hill", neighborhoods: "Oak Ridges, Richvale, Mill Pond" },
  { area: "Markham", neighborhoods: "Unionville, Thornhill, Cornell, Markham Village" },
  { area: "Vaughan", neighborhoods: "Woodbridge, Maple, Concord, Kleinburg" },
  { area: "Oakville", neighborhoods: "Downtown, Bronte, Glen Abbey" },
  { area: "Burlington", neighborhoods: "Downtown, Aldershot, Appleby" },
  { area: "Ajax & Pickering", neighborhoods: "Downtown Ajax, West Rouge, Liverpool" }
];

const faqs = [
  {
    question: "What areas do you service?",
    answer: "We service the entire Greater Toronto Area (GTA) including Toronto, North York, Scarborough, Etobicoke, Mississauga, Brampton, Richmond Hill, Markham, Vaughan, Oakville, Burlington, Ajax, and Pickering."
  },
  {
    question: "How do I book a cleaning?",
    answer: "You can book through our online booking system, call us at (647) 870-0445, send us an email, or message us on WhatsApp. We'll confirm your appointment within a few hours."
  },
  {
    question: "Are your cleaners insured and background-checked?",
    answer: "Yes! All our cleaning professionals are fully insured, bonded, and have passed thorough background checks. Your safety and peace of mind are our top priorities."
  },
  {
    question: "What cleaning products do you use?",
    answer: "We use eco-friendly, professional-grade cleaning products that are safe for families, pets, and the environment. If you have specific preferences or allergies, please let us know."
  },
  {
    question: "Can I request the same cleaner each time?",
    answer: "Absolutely! We encourage building relationships with our clients. Just let us know your preference and we'll do our best to accommodate consistent cleaner assignments."
  },
  {
    question: "What's your cancellation policy?",
    answer: "We require 24-hour notice for cancellations to avoid a cancellation fee. We understand emergencies happen and handle each situation with understanding."
  },
  {
    question: "Do you bring your own equipment?",
    answer: "Yes, we bring all necessary cleaning supplies and equipment. However, we recommend using your vacuum for homes with pets to avoid cross-contamination."
  },
  {
    question: "How long does a cleaning take?",
    answer: "Cleaning duration depends on the size and condition of your property. A standard cleaning for a 2-bedroom apartment typically takes 2-3 hours. Deep cleans take longer."
  }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, service_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Contact Us
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              Have questions or ready to book? We're here to help. Reach out to us and we'll respond within a few hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0C0A09] mb-8">
                Send Us a Message
              </h2>
              
              {isSubmitted ? (
                <div className="bg-[#F0FDF4] p-8 rounded-xl border border-[#064E3B]/20 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-[#064E3B]" />
                  <h3 className="text-xl font-semibold text-[#0C0A09] mb-2">Message Sent!</h3>
                  <p className="text-[#78716C] mb-6">
                    Thank you for contacting us. We'll get back to you within a few hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 bg-white border-[#E7E5E4] focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 bg-white border-[#E7E5E4] focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="h-12 bg-white border-[#E7E5E4] focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                        placeholder="(647) 000-0000"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Interest</Label>
                      <Select onValueChange={handleSelectChange} value={formData.service_interest}>
                        <SelectTrigger className="h-12 bg-white border-[#E7E5E4]" data-testid="contact-service">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residential">Residential Cleaning</SelectItem>
                          <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                          <SelectItem value="airbnb">Airbnb Cleaning</SelectItem>
                          <SelectItem value="post-renovation">Post-Renovation Cleaning</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-white border-[#E7E5E4] focus:border-[#064E3B] focus:ring-[#064E3B]/20 resize-none"
                      placeholder="Tell us about your cleaning needs..."
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full py-6 text-lg font-medium shadow-lg shadow-emerald-900/20"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-[#0C0A09] mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                <a
                  href="tel:+16478700445"
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-[#E7E5E4] hover:border-[#064E3B] transition-colors"
                  data-testid="contact-phone-card"
                >
                  <div className="w-12 h-12 rounded-full bg-[#064E3B] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#C0A062]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0C0A09] mb-1">Phone</h3>
                    <p className="text-[#064E3B] font-medium">(647) 870-0445</p>
                    <p className="text-[#78716C] text-sm">Call or text anytime</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/16478700445"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-[#E7E5E4] hover:border-[#25D366] transition-colors"
                  data-testid="contact-whatsapp-card"
                >
                  <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0C0A09] mb-1">WhatsApp</h3>
                    <p className="text-[#25D366] font-medium">Message Us</p>
                    <p className="text-[#78716C] text-sm">Quick responses guaranteed</p>
                  </div>
                </a>

                <a
                  href="mailto:torontopclean@gmail.com"
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-[#E7E5E4] hover:border-[#064E3B] transition-colors"
                  data-testid="contact-email-card"
                >
                  <div className="w-12 h-12 rounded-full bg-[#064E3B] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#C0A062]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0C0A09] mb-1">Email</h3>
                    <p className="text-[#064E3B] font-medium">torontopclean@gmail.com</p>
                    <p className="text-[#78716C] text-sm">We reply within hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-[#E7E5E4]">
                  <div className="w-12 h-12 rounded-full bg-[#064E3B] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#C0A062]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0C0A09] mb-1">Business Hours</h3>
                    <p className="text-[#78716C] text-sm">Monday - Saturday: 7:00 AM - 8:00 PM</p>
                    <p className="text-[#78716C] text-sm">Sunday: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
              Service Areas
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              Proudly Serving the Greater Toronto Area
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceAreas.map((item, index) => (
              <div
                key={item.area}
                className="p-6 bg-[#FAFAF9] rounded-xl border border-[#E7E5E4] hover:border-[#064E3B]/50 transition-colors"
                data-testid={`service-area-${index}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-[#064E3B]" />
                  <h3 className="font-semibold text-[#0C0A09]">{item.area}</h3>
                </div>
                <p className="text-[#78716C] text-sm pl-8">{item.neighborhoods}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-[#FAFAF9]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              Frequently Asked Questions
            </h2>
            <div className="section-divider" />
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-xl border border-[#E7E5E4] px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#0C0A09] hover:text-[#064E3B] hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#78716C] pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
